import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { fetchOrganizers } from '../redux/slices/organizersSlice';
import { fetchVouchers, updateVoucherStatus } from '../redux/slices/vouchersSlice';
import NoData from '../components/NoData';
import VoucherDrawer from '../components/VoucherDrawer';
import {
  formatVoucherDate,
  formatVoucherDiscount,
  getVoucherStatus,
  getVoucherStatusColor,
  VOUCHER_STATUSES,
} from '../utils/voucherUtils';

function VoucherActions({ voucher }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const close = () => setAnchorEl(null);

  const toggleStatus = async () => {
    close();
    try {
      await dispatch(updateVoucherStatus({ voucherId: voucher.id, active: !Boolean(voucher.active) })).unwrap();
      toast.success(t(voucher.active ? 'Voucher deactivated successfully' : 'Voucher activated successfully'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch {
      toast.error(t('Voucher status could not be changed'), { position: toast.POSITION.BOTTOM_RIGHT });
    }
  };

  return (
    <>
      <IconButton
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem onClick={toggleStatus}>{t(voucher.active ? 'Deactivate' : 'Activate')}</MenuItem>
      </Menu>
    </>
  );
}

export default function Vouchers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const vouchers = useSelector((state) => state.vouchers.data);
  const organizers = useSelector((state) => state.organizers.data);
  const isLoading = useSelector((state) => state.vouchers.isLoading);
  const error = useSelector((state) => state.vouchers.error);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [organizerId, setOrganizerId] = useState('');

  useEffect(() => {
    dispatch(fetchVouchers());
    if (user.role === 'admin') dispatch(fetchOrganizers());
  }, [dispatch, user.role]);

  useEffect(() => {
    if (error) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [error, t]);

  const organizerNames = useMemo(() => new Map(organizers.map(({ id, name }) => [Number(id), name])), [organizers]);

  const filteredVouchers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();

    return vouchers.filter((voucher) => {
      const voucherStatus = getVoucherStatus(voucher);
      const matchesStatus = status === 'ALL' || voucherStatus === status;
      const matchesOrganizer = !organizerId || Number(voucher.organizers_id) === Number(organizerId);
      const matchesSearch =
        !query ||
        voucher.name.toLocaleLowerCase().includes(query) ||
        String(voucher.id).includes(query) ||
        organizerNames.get(Number(voucher.organizers_id))?.toLocaleLowerCase().includes(query);

      return matchesStatus && matchesOrganizer && matchesSearch;
    });
  }, [organizerId, organizerNames, search, status, vouchers]);

  return (
    <div className="vouchers">
      <Grid container spacing={2}>
        <Grid item container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h3">{t('Vouchers')}</Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={() => setDrawerOpen(true)}>
              {t('Add voucher')}
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              label={t('Search vouchers')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>{t('Status')}</InputLabel>
              <Select value={status} label={t('Status')} onChange={(event) => setStatus(event.target.value)}>
                <MenuItem value="ALL">{t('All statuses')}</MenuItem>
                {Object.values(VOUCHER_STATUSES).map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {user.role === 'admin' && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>{t('Organizer')}</InputLabel>
                <Select
                  value={organizerId}
                  label={t('Organizer')}
                  onChange={(event) => setOrganizerId(event.target.value)}
                >
                  <MenuItem value="">{t('All organizers')}</MenuItem>
                  {organizers.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          {isLoading && vouchers.length === 0 ? (
            <Box sx={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredVouchers.length ? (
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& > th': { whiteSpace: 'nowrap' } }}>
                      <TableCell sx={{ width: 80 }}>{t('ID')}</TableCell>
                      <TableCell>{t('Name')}</TableCell>
                      {user.role === 'admin' && <TableCell>{t('Organizer')}</TableCell>}
                      <TableCell>{t('Discount')}</TableCell>
                      <TableCell>{t('Validity')}</TableCell>
                      <TableCell>{t('Eligible events')}</TableCell>
                      <TableCell align="right">{t('Codes')}</TableCell>
                      <TableCell align="center">{t('Status')}</TableCell>
                      <TableCell align="right">{t('Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredVouchers.map((voucher) => {
                      const voucherStatus = getVoucherStatus(voucher);
                      const validity = [formatVoucherDate(voucher.valid_from), formatVoucherDate(voucher.valid_until)]
                        .filter(Boolean)
                        .join(' — ');

                      return (
                        <TableRow
                          hover
                          component={RouterLink}
                          to={`${voucher.id}`}
                          key={voucher.id}
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:nth-of-type(odd)': (theme) => ({ backgroundColor: theme.palette.action.hover }),
                          }}
                        >
                          <TableCell>{voucher.id}</TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">{voucher.name}</Typography>
                          </TableCell>
                          {user.role === 'admin' && (
                            <TableCell>{organizerNames.get(Number(voucher.organizers_id)) || '-'}</TableCell>
                          )}
                          <TableCell>{formatVoucherDiscount(voucher, t)}</TableCell>
                          <TableCell>{validity || t('No date limit')}</TableCell>
                          <TableCell>
                            {voucher.event_scope === 'ALL_ORGANIZER_EVENTS'
                              ? t('All organizer events')
                              : t('{{count}} selected events', { count: Number(voucher.selected_event_count) })}
                          </TableCell>
                          <TableCell align="right">{Number(voucher.code_count)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              variant="outlined"
                              color={getVoucherStatusColor(voucherStatus)}
                              label={t(voucherStatus)}
                              sx={{ borderRadius: 1 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <VoucherActions voucher={voucher} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            <NoData />
          )}
        </Grid>
      </Grid>
      <VoucherDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onSaved={({ id }) => navigate(`${id}`)} />
    </div>
  );
}
